/**
 * The MFA methods supported by the API.
 *
 * @type {Object}
 */
export const MFA_METHODS = {
    TOTP: 'TOTP',
};

/**
 * The `status` values returned by `POST /v2/oauth/authenticate` when MFA is involved.
 * `MFA_ENROLLED` -> the user has a method enrolled and must verify a code.
 * `MFA_REQUIRED` -> the user has no method enrolled and must set one up first.
 *
 * @type {Object}
 */
export const MFA_STATUS = {
    MFA_ENROLLED: 'MFA_ENROLLED',
    MFA_REQUIRED: 'MFA_REQUIRED',
};

/**
 * The `status` values that require the user to complete an MFA flow before signing in.
 *
 * @type {Array}
 */
export const MFA_REQUIRED_STATUSES = [MFA_STATUS.MFA_ENROLLED, MFA_STATUS.MFA_REQUIRED];

/**
 * The routes of the MFA screens.
 * `SETUP`          -> where a user without an enrolled method sets one up.
 * `VERIFY_IDENTITY` -> where an enrolled user enters the verification code.
 *
 * @type {Object}
 */
export const MFA_ROUTES = {
    SETUP: '/auth/mfa/setup',
    VERIFY_IDENTITY: '/auth/mfa/verify-identity',
};

/**
 * The MFA route the user is sent to for each authenticate `status`.
 *
 * @type {Object}
 */
export const MFA_ROUTE_BY_STATUS = {
    [MFA_STATUS.MFA_ENROLLED]: MFA_ROUTES.VERIFY_IDENTITY,
    [MFA_STATUS.MFA_REQUIRED]: MFA_ROUTES.SETUP,
};

/**
 * The keys of the login credentials that are needed to complete the
 * post sign in redirect once the MFA code has been verified.
 *
 * NOTE: the user's password is never part of this list.
 *
 * @type {Array}
 */
export const MFA_LOGIN_CONTEXT_KEYS = [
    'redirect',
    'redirectState',
    'reload',
    'rememberUsername',
    'username',
];

/**
 * The number of digits of a TOTP verification code.
 *
 * @type {number}
 */
export const MFA_CODE_LENGTH = 6;

/**
 * The copy of the MFA verify identity screen.
 *
 * @type {Object}
 */
export const MFA_VERIFY_IDENTITY = {
    CANT_ACCESS_CODE: 'Can’t access your code?',
    CODE_INPUT_LABEL: 'Digit DIGIT_POSITION of MFA verification code',
    SUBTITLE: 'Enter the 6-digit code from your authenticator app.',
    TITLE: 'Verify Your Identity',
    VERIFY: 'Verify',
};

/**
 * The `errorCode` values, they are strings in the API responses, the verify API
 * answers a failed verification with.
 *
 * @type {Object}
 */
export const MFA_ERROR_CODES = {
    INVALID_CODE: '1062',
    INVALID_TOKEN: '1061',
    MAX_ATTEMPTS: '1075',
    SESSION_EXPIRED: '1065',
    TOKEN_REPLAYED: '1063',
    TOKEN_REVOKED: '1076',
};

/**
 * The error states of the verify identity screen.
 * `invalidCode`    -> the code was rejected, the user can try again.
 * `maxAttempts`    -> every attempt of the challenge was used up.
 * `sessionExpired` -> the MFA token is gone, expired, replayed or revoked.
 *
 * @type {Object}
 */
export const MFA_ERROR_TYPES = {
    INVALID_CODE: 'invalidCode',
    MAX_ATTEMPTS: 'maxAttempts',
    SESSION_EXPIRED: 'sessionExpired',
};

/**
 * The error state of the screen for each `errorCode` of the verify API.
 * Any code that is not listed here is rendered with the message of the API
 * itself and leaves the screen in its default, retryable, state.
 *
 * @type {Object}
 */
export const MFA_ERROR_TYPE_BY_CODE = {
    [MFA_ERROR_CODES.INVALID_CODE]: MFA_ERROR_TYPES.INVALID_CODE,
    [MFA_ERROR_CODES.INVALID_TOKEN]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.MAX_ATTEMPTS]: MFA_ERROR_TYPES.MAX_ATTEMPTS,
    [MFA_ERROR_CODES.SESSION_EXPIRED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.TOKEN_REPLAYED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.TOKEN_REVOKED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
};

/**
 * The error states the user cannot recover from on the screen itself, they
 * have to start over from the login screen.
 *
 * @type {Array}
 */
export const MFA_TERMINAL_ERROR_TYPES = [
    MFA_ERROR_TYPES.MAX_ATTEMPTS,
    MFA_ERROR_TYPES.SESSION_EXPIRED,
];

/**
 * The message rendered when the code is rejected, the user can try again.
 *
 * @type {string}
 */
export const MFA_INVALID_CODE_ERROR = 'The code you entered is invalid. Please try again.';

/**
 * The message rendered when every attempt of the challenge was used up.
 *
 * @type {string}
 */
export const MFA_MAX_ATTEMPTS_ERROR = 'Too many failed attempts. Please sign in again.';

/**
 * The message rendered when the MFA session is no longer usable.
 *
 * @type {string}
 */
export const MFA_SESSION_EXPIRED_ERROR = 'Your session has expired. Please sign in again.';

/**
 * The counter rendered above the inputs after a rejected code, `ATTEMPTS_LEFT`
 * is replaced with the `remainingAttempts` of the response.
 *
 * @type {Object}
 */
export const MFA_ATTEMPTS_REMAINING = {
    PLURAL: 'You have ATTEMPTS_LEFT attempts to enter the correct code.',
    SINGULAR: 'You have ATTEMPTS_LEFT attempt to enter the correct code.',
};

/**
 * The copy of the CTA that takes the user back to the login screen once the
 * MFA challenge can no longer be completed.
 *
 * @type {string}
 */
export const MFA_BACK_TO_SIGN_IN = 'Back to Sign In';

/**
 * The duration, in milliseconds, of the animation the code inputs play when the
 * typed code is rejected.
 *
 * @type {number}
 */
export const MFA_INVALID_CODE_ANIMATION_DURATION = 300;
